import { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PrintLab, SheetLibrary, type AssetUploader, type SheetRenderer } from '@eprintsmart/print-lab';
import '@eprintsmart/print-lab/print-lab.css';
import { auth, db } from '../firebase';

/**
 * The print lab: gang artwork onto a press sheet and save the layout.
 *
 * A pre-press tool. It used to be customer self-serve on the storefront, which
 * asked customers to do their own imposition; the shop and the printer are the
 * ones who need it.
 */
const renderSheet: SheetRenderer = async (input) => {
  const call = httpsCallable<typeof input, { url: string; storagePath?: string }>(
    getFunctions(undefined, 'asia-south1'),
    'renderImpositionSheet',
  );
  return (await call(input)).data;
};

const uploadAsset: AssetUploader = async (input) => {
  const call = httpsCallable<typeof input, Awaited<ReturnType<AssetUploader>>>(
    getFunctions(undefined, 'asia-south1'),
    'createDesignAssetUploadUrl',
  );
  return (await call(input)).data;
};

export function PrintLabPage() {
  // The AuthGate above guarantees a user, but currentUser is null for a tick
  // while Firebase rehydrates -- rendering with an empty uid would stamp the
  // saved sheet with no author.
  const [uid, setUid] = useState(auth.currentUser?.uid ?? null);
  useEffect(() => auth.onAuthStateChanged((u) => setUid(u?.uid ?? null)), []);

  if (!uid) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Lay artwork out on a press sheet. Saved sheets are shared across the shop.
      </Alert>
      <PrintLab db={db} uid={uid} uploadAsset={uploadAsset} savedJobsHref="#saved-sheets" />

      <Box id="saved-sheets" sx={{ mt: 4 }}>
        <Typography variant="h2" sx={{ fontSize: '1.1rem', mb: 1 }}>Saved sheets</Typography>
        <SheetLibrary db={db} render={renderSheet} canDelete />
      </Box>
    </Box>
  );
}
